/**
 * SEO Utility Library
 * 
 * Comprehensive SEO management for tensionlabs.net
 * - Dynamic meta tags
 * - Structured data (Schema.org)
 * - Open Graph & Twitter Cards
 * - Canonical URLs
 */

import { useEffect } from 'react';

/** Resolves on production (unlike /logo.png which 404s today). */
export const SITE_LOGO_ABSOLUTE_URL = 'https://tensionlabs.net/tension-logo.png';

/**
 * Fallback OG/Twitter/schema image when a page has no dedicated asset.
 * Curated Unsplash (aligned with marketing stock) so social crawlers get HTTP 200.
 */
export const SITE_DEFAULT_OG_IMAGE_URL =
  'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?w=1200&h=630&fit=crop&q=85&auto=format&ixlib=rb-4.0.3';

export interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  article?: {
    author?: string;
    section?: string;
    tags?: string[];
  };
  openGraph?: {
    title?: string;
    description?: string;
    url?: string;
    type?: 'website' | 'article' | 'product';
    siteName?: string;
  };
  twitter?: {
    card?: 'summary' | 'summary_large_image' | 'app' | 'player';
    title?: string;
    description?: string;
  };
  robots?: {
    index?: boolean;
    follow?: boolean;
  };
  noIndex?: boolean;
}

export interface StructuredDataConfig {
  type: 'Article' | 'BlogPosting' | 'Product' | 'FAQPage' | 'HowTo' | 'Organization' | 'WebApplication';
  data: Record<string, any>;
}

/**
 * High-intent keyword clusters for relationship/dating/conflict app SEO.
 * Use these as a base and append campaign-specific target terms.
 */
export const COMPETITIVE_KEYWORD_CLUSTERS = {
  core: [
    'relationship app',
    'best relationship app',
    'couples app',
    'relationship counseling app',
    'relationship tracker',
    'relationship intelligence app',
    'relationship health app',
  ],
  conflict: [
    'conflict resolution app',
    'relationship conflict app',
    'couples conflict resolution tools',
    'de-escalation communication app',
    'repair relationship after argument',
  ],
  communication: [
    'communication coach app',
    'message rewrite app',
    'ai couples therapy',
    'relationship communication tools',
    'how to communicate better in relationships',
    'couples communication app',
  ],
  dating: [
    'connection anxiety app',
    'relationship anxiety app',
    'relationship readiness app',
    'connection pattern tracker',
    'connection red flags app',
  ],
  alternatives: [
    'relationship app alternatives',
    'best app for couples communication',
    'apps like relationship tracker',
  ],
} as const;

/**
 * Canonical keyword ownership map to avoid keyword cannibalization across pages.
 * Secondary pages can still mention these terms, but this route should be the primary target.
 */
export const PRIMARY_KEYWORD_TARGETS: Record<string, string> = {
  'relationship app': '/best-relationship-app',
  'best relationship app': '/best-relationship-app',
  'couples app': '/best-couples-app',
  'best couples app': '/best-couples-app',
  'relationship counseling app': '/relationship-counseling-app',
  'ai couples therapy': '/ai-couples-therapy',
  'paired alternative': '/alternatives/paired',
  'apps like paired': '/alternatives/paired',
  'tension vs paired': '/compare/tension-vs-paired',
} as const;

export function mergeKeywordSets(...sets: Array<Array<string>>): Array<string> {
  return Array.from(new Set(sets.flat().map((k) => String(k).trim()).filter(Boolean)));
}

/**
 * SEO Hook - Updates meta tags dynamically
 */
export function useSEO(config: SEOConfig) {
  useEffect(() => {
    const baseUrl = 'https://tensionlabs.net';
    const canonicalUrl = config.canonical
      ? /^https?:\/\//i.test(config.canonical)
        ? config.canonical
        : `${baseUrl}${config.canonical}`
      : undefined;
    
    // Update title
    if (config.title) {
      document.title = config.title;
      updateMetaTag('property', 'og:title', config.title);
      updateMetaTag('name', 'twitter:title', config.title);
    }
    
    // Update description
    if (config.description) {
      updateMetaTag('name', 'description', config.description);
      updateMetaTag('property', 'og:description', config.description);
      updateMetaTag('name', 'twitter:description', config.description);
    }
    
    // Update keywords
    if (config.keywords && config.keywords.length > 0) {
      updateMetaTag('name', 'keywords', config.keywords.join(', '));
    }
    
    // Update canonical URL
    if (canonicalUrl) {
      updateLinkTag('canonical', canonicalUrl);
      updateMetaTag('property', 'og:url', canonicalUrl);
      updateMetaTag('name', 'twitter:url', canonicalUrl);
    }
    
    // Update OG image
    if (config.ogImage) {
      updateMetaTag('property', 'og:image', config.ogImage);
      updateMetaTag('name', 'twitter:image', config.ogImage);
    }
    
    // Update OG type
    if (config.ogType) {
      updateMetaTag('property', 'og:type', config.ogType);
    }

    if (config.openGraph) {
      if (config.openGraph.title) {
        updateMetaTag('property', 'og:title', config.openGraph.title);
      }
      if (config.openGraph.description) {
        updateMetaTag('property', 'og:description', config.openGraph.description);
      }
      if (config.openGraph.url) {
        updateMetaTag('property', 'og:url', config.openGraph.url);
      }
      if (config.openGraph.type) {
        updateMetaTag('property', 'og:type', config.openGraph.type);
      }
      if (config.openGraph.siteName) {
        updateMetaTag('property', 'og:site_name', config.openGraph.siteName);
      }
    }
    
    // Update Twitter card
    if (config.twitterCard) {
      updateMetaTag('name', 'twitter:card', config.twitterCard);
    }

    if (config.twitter) {
      if (config.twitter.card) {
        updateMetaTag('name', 'twitter:card', config.twitter.card);
      }
      if (config.twitter.title) {
        updateMetaTag('name', 'twitter:title', config.twitter.title);
      }
      if (config.twitter.description) {
        updateMetaTag('name', 'twitter:description', config.twitter.description);
      }
    }
    
    // Update author
    if (config.author) {
      updateMetaTag('name', 'author', config.author);
      updateMetaTag('property', 'article:author', config.author);
    }
    
    // Update published time
    if (config.publishedTime) {
      updateMetaTag('property', 'article:published_time', config.publishedTime);
    }
    
    // Update modified time
    if (config.modifiedTime) {
      updateMetaTag('property', 'article:modified_time', config.modifiedTime);
    }
    
    // Update article tags
    if (config.article?.tags) {
      config.article.tags.forEach((tag, index) => {
        updateMetaTag('property', `article:tag${index}`, tag);
      });
    }
    
    // Update robots
    if (config.robots) {
      const robotsValue = `${config.robots.index === false ? 'noindex' : 'index'}, ${config.robots.follow === false ? 'nofollow' : 'follow'}`;
      updateMetaTag('name', 'robots', robotsValue);
    } else if (config.noIndex) {
      updateMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      updateMetaTag('name', 'robots', 'index, follow');
    }
    
  }, [config]);
}

/**
 * Structured Data Hook - Adds JSON-LD schema
 */
export function useStructuredData(config: StructuredDataConfig) {
  useEffect(() => {
    const scriptId = 'structured-data-script';
    
    // Remove existing structured data
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }
    
    // Create new structured data
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': config.type,
      ...config.data,
    });
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [config]);
}

/**
 * Helper: Update or create meta tag
 */
function updateMetaTag(attribute: string, name: string, content: string) {
  let element = document.querySelector(`meta[${attribute}="${name}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
}

/**
 * Helper: Update or create link tag
 */
function updateLinkTag(rel: string, href: string) {
  let element = document.querySelector(`link[rel="${rel}"]`);
  
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  
  element.setAttribute('href', href);
}

/**
 * SEO Presets for common pages
 */
export const SEOPresets = {
  home: {
    title: 'TENSION - Relationship Tension Monitor | Track, Forecast & Stabilize Tension',
    description: 'Measure, forecast, and stabilize relationship tension before it causes emotional distance. Science-backed RTI scoring tracks 5 behavioral components with personalized action cards.',
    keywords: [
      'relationship anxiety',
      'relationship app',
      'attachment theory',
      'relationship tracking',
      'emotional distance',
      'connection anxiety',
      'relationship tension',
      'couples app',
      'relationship health',
      'anxiety relief',
    ],
    canonical: '/',
    ogImage: SITE_DEFAULT_OG_IMAGE_URL,
    ogType: 'website' as const,
    twitterCard: 'summary_large_image' as const,
  },
  
  pricing: {
    title: 'TENSION Pricing - Free Trial | Premium Relationship Insights',
    description: 'Start free with basic tension tracking. Upgrade to Premium for advanced forecasting, unlimited action cards, and deeper insights. No credit card required.',
    keywords: [
      'relationship app pricing',
      'couples app cost',
      'relationship therapy app',
      'anxiety app subscription',
      'relationship tools pricing',
    ],
    canonical: '/pricing',
    ogType: 'product' as const,
  },
  
  about: {
    title: 'About TENSION - Science-Backed Relationship Tension Monitor',
    description: 'Learn how TENSION uses behavioral science and attachment theory to help couples identify, forecast, and reduce relationship tension before emotional distance occurs.',
    keywords: [
      'relationship science',
      'attachment theory app',
      'relationship psychology',
      'behavioral tracking',
      'relationship research',
    ],
    canonical: '/about',
  },
  
  faq: {
    title: 'FAQ - Frequently Asked Questions About TENSION',
    description: 'Get answers about how TENSION works, privacy, pricing, and relationship tension tracking. Learn about RTI scoring, action cards, and our science-backed approach.',
    keywords: [
      'relationship app faq',
      'tension tracking questions',
      'relationship anxiety help',
      'RTI score explained',
    ],
    canonical: '/faq',
  },
  
  blog: {
    title: 'TENSION Blog - Relationship Advice & Anxiety Tips',
    description: 'Expert advice on managing relationship anxiety, understanding attachment theory, reducing emotional distance, and building healthier connections.',
    keywords: [
      'relationship advice',
      'relationship readiness tips',
      'anxiety management',
      'attachment theory',
      'relationship psychology',
      'couples therapy',
    ],
    canonical: '/blog',
    ogType: 'website' as const,
  },
};

/**
 * Blog Article SEO Generator
 */
export function generateBlogSEO(article: {
  title: string;
  description: string;
  slug: string;
  author: string;
  publishedAt: string;
  modifiedAt?: string;
  tags: string[];
  image?: string;
}): SEOConfig {
  return {
    title: `${article.title} | TENSION Blog`,
    description: article.description,
    keywords: article.tags,
    canonical: `/blog/${article.slug}`,
    ogImage: article.image || SITE_DEFAULT_OG_IMAGE_URL,
    ogType: 'article',
    twitterCard: 'summary_large_image',
    author: article.author,
    publishedTime: article.publishedAt,
    modifiedTime: article.modifiedAt,
    article: {
      author: article.author,
      section: 'Relationships',
      tags: article.tags,
    },
  };
}

/**
 * FAQ Page Structured Data Generator
 */
export function generateFAQStructuredData(faqs: Array<{ question: string; answer: string }>): StructuredDataConfig {
  return {
    type: 'FAQPage',
    data: {
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  };
}

/**
 * Article Structured Data Generator
 */
export function generateArticleStructuredData(article: {
  headline: string;
  description: string;
  author: string;
  publishedAt: string;
  modifiedAt?: string;
  image?: string;
}): StructuredDataConfig {
  return {
    type: 'BlogPosting',
    data: {
      headline: article.headline,
      description: article.description,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: 'TENSION',
        logo: {
          '@type': 'ImageObject',
          url: SITE_LOGO_ABSOLUTE_URL,
        },
      },
      datePublished: article.publishedAt,
      dateModified: article.modifiedAt || article.publishedAt,
      image: article.image || SITE_DEFAULT_OG_IMAGE_URL,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': 'https://tensionlabs.net',
      },
    },
  };
}

/**
 * Product/App Structured Data
 */
export function generateAppStructuredData(): StructuredDataConfig {
  return {
    type: 'WebApplication',
    data: {
      name: 'TENSION',
      applicationCategory: 'LifestyleApplication',
      operatingSystem: 'Web, iOS, Android',
      description: 'Measure, forecast, and stabilize relationship tension before it causes emotional distance. Science-backed RTI scoring system with personalized action cards.',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      screenshot: SITE_DEFAULT_OG_IMAGE_URL,
      author: {
        '@type': 'Organization',
        name: 'TENSION Labs',
        url: 'https://tensionlabs.net',
      },
    },
  };
}
